import { useCallback, useState } from "react";

import { IFilterWithValues } from "../../List/components/FilterModal";
import { ContactSelectType } from "../schema/contacts";

interface UseOrganizationSelectionOptions {
	/** The rows currently on screen — used to select/deselect the whole page. */
	data?: { id: number }[];
	/** Total rows matching the current filter/search, for the "select all" count. */
	itemCount?: number;
	/** Active advanced filter, sent along when the whole result set is selected. */
	filter?: IFilterWithValues;
	/** Debounced search term, sent along when the whole result set is selected. */
	query?: string;
}

/**
 * Row-selection state shared by the organization tables (Companies / Competitors):
 * which ids are ticked, whether the selection means "this page" or "everything that
 * matches", and the payload every bulk action (add to list, tracker, export) sends.
 */
export const useOrganizationSelection = ({
	data,
	itemCount,
	filter,
	query,
}: UseOrganizationSelectionOptions) => {
	const [selectedItems, setSelectedItems] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [selectType, setSelectType] = useState<ContactSelectType>(ContactSelectType.CURRENT_PAGE);
	const [prepSelectType, setPrepSelectType] = useState<ContactSelectType>(ContactSelectType.ALL);

	const getSelectionPayload = useCallback(() => {
		if (selectType === ContactSelectType.ALL) {
			return {
				selectType: ContactSelectType.ALL,
				...(filter && Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
				...(query && { searchValue: query }),
			};
		}

		return {
			selectType: ContactSelectType.CURRENT_PAGE,
			ids: selectedItems,
		};
	}, [selectType, selectedItems, filter, query]);

	const toggleSelectAll = useCallback(() => {
		if (selectAll) {
			setSelectedItems([]);
			setSelectAll(false);
		} else {
			setSelectedItems(data?.map((row) => row.id) || []);
			setSelectAll(true);
		}
	}, [selectAll, data]);

	const handleSetSelectType = useCallback(
		(type: ContactSelectType) => {
			setSelectedItems(data?.map((row) => row.id) || []);
			setSelectAll(true);
			setShowAll(false);
			setSelectType(type);
			setPrepSelectType(ContactSelectType.ALL);
		},
		[data]
	);

	const handleSelectItem = useCallback((id: number) => {
		setSelectedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
		setSelectAll(false);
		setSelectType(ContactSelectType.CURRENT_PAGE);
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedItems([]);
		setSelectAll(false);
		setSelectType(ContactSelectType.CURRENT_PAGE);
	}, []);

	return {
		selectedItems,
		selectAll,
		selectType,
		showAll,
		setShowAll,
		prepSelectType,
		setPrepSelectType,
		hasSelection: selectedItems.length > 0,
		selectedCount: selectType === ContactSelectType.ALL ? itemCount || 0 : selectedItems.length,
		getSelectionPayload,
		toggleSelectAll,
		handleSetSelectType,
		handleSelectItem,
		clearSelection,
	};
};
